import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus, Send, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { type ColumnDef, DataTable, DataTableColumnHeader } from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NoticeBanner } from "@/components/ui/notice-banner";
import {
	type AlertKind,
	type AlertLogItem,
	type AlertSettings,
	type AlertStatus,
	formatDateTime,
	helpdeskApi,
	useHelpdeskKey,
	useHelpdeskMutation,
} from "./api";
import { useTableExport } from "./use-table-export";

const MAX_RECIPIENTS = 10;

export const ALERT_KIND_LABEL: Record<AlertKind, string> = {
	ticket: "Zgłoszenie",
	incident: "Awaria",
	exhausted: "Limit wyczerpany",
	test: "Test",
};

const ALERT_STATUS: Record<AlertStatus, { label: string; className: string }> = {
	sent: { label: "wysłany", className: "text-status-success-text" },
	partial: { label: "częściowo", className: "text-status-warning-text" },
	failed: { label: "błąd", className: "text-status-error-text" },
};

const LOG_COLUMN_LABELS: Record<string, string> = {
	createdAt: "Kiedy",
	kind: "Rodzaj",
	tickets: "Zgłoszenia",
	status: "Status",
	delivered: "Wysłane",
	reason: "Powód",
	body: "Treść SMS",
	error: "Błąd",
};

/** Frazy w formularzu jako tekst, jedna w linii; reszta jak w API. */
type Form = Omit<AlertSettings, "phrases"> & { phrases: string };

const toForm = (s: AlertSettings): Form => ({ ...s, phrases: s.phrases.join("\n") });

export const fromForm = (f: Form): AlertSettings => ({
	...f,
	phrases: f.phrases
		.split("\n")
		.map((p) => p.trim())
		.filter(Boolean),
});

const selectClass =
	"h-9 w-24 rounded-md border border-input bg-background px-2 text-sm tabular-nums";
/** Pełne godziny 0–24 (okno `[od, do)`). */
const HOURS = Array.from({ length: 25 }, (_, h) => h);

function HourRange({
	label,
	from,
	to,
	onChange,
}: {
	label: string;
	from: number;
	to: number;
	onChange: (from: number, to: number) => void;
}) {
	return (
		<div className="space-y-1">
			<span className="text-xs font-medium">{label}</span>
			<div className="flex items-center gap-2 text-sm">
				<select
					aria-label={`${label} — od`}
					value={from}
					onChange={(e) => onChange(Number(e.target.value), to)}
					className={selectClass}
				>
					{HOURS.slice(0, 24).map((h) => (
						<option key={h} value={h}>
							{h}:00
						</option>
					))}
				</select>
				<span className="text-muted-foreground">do</span>
				<select
					aria-label={`${label} — do`}
					value={to}
					onChange={(e) => onChange(from, Number(e.target.value))}
					className={selectClass}
				>
					{HOURS.slice(1).map((h) => (
						<option key={h} value={h}>
							{h}:00
						</option>
					))}
				</select>
			</div>
		</div>
	);
}

function NumberField({
	id,
	label,
	value,
	onChange,
}: {
	id: string;
	label: string;
	value: number;
	onChange: (v: number) => void;
}) {
	return (
		<div className="space-y-1">
			<label htmlFor={id} className="text-xs font-medium">
				{label}
			</label>
			<Input
				id={id}
				type="number"
				min={1}
				value={String(value)}
				onChange={(e) => onChange(Number(e.target.value))}
				className="h-9 text-sm tabular-nums"
			/>
		</div>
	);
}

/**
 * Alert SMS do adminów o krytycznych zgłoszeniach (konfiguracja → „Alert SMS”):
 * wyłącznik, okno godzin, odbiorcy, frazy zapasowe, limity, test i dziennik prób.
 * Reguły i walidacja po stronie serwera (`helpdesk::alerts`).
 */
export function AlertsPanel() {
	const key = useHelpdeskKey();
	const query = useQuery({
		queryKey: key("settings", "alerts"),
		queryFn: () => helpdeskApi.settings<AlertSettings>("alerts"),
	});
	const log = useQuery({
		queryKey: key("alert-log"),
		queryFn: helpdeskApi.alertLog,
		staleTime: 30_000,
	});
	const [form, setForm] = useState<Form | null>(null);
	useEffect(() => {
		if (query.data) setForm(toForm(query.data));
	}, [query.data]);
	const save = useHelpdeskMutation(
		(body: AlertSettings) => helpdeskApi.saveSettings("alerts", body),
		{ success: "Zapisano ustawienia alertu SMS", error: "Nie udało się zapisać ustawień" },
	);
	const [confirmTest, setConfirmTest] = useState(false);
	const test = useHelpdeskMutation(helpdeskApi.testAlert, {
		success: (r) =>
			r.status === "sent"
				? "Wysłano testowy SMS"
				: `Test zapisany ze stanem: ${ALERT_STATUS[r.status].label}`,
		error: "Nie udało się wysłać testowego SMS",
	});

	if (query.isError) return <NoticeBanner tone="error" title="Nie udało się pobrać ustawień" />;
	if (!form || !query.data) return <p className="text-xs text-muted-foreground">Wczytywanie…</p>;
	const set = (patch: Partial<Form>) => setForm({ ...form, ...patch });
	const setRecipient = (i: number, patch: Partial<Form["recipients"][number]>) =>
		set({ recipients: form.recipients.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
	const saved = query.data;
	const dirty = JSON.stringify(fromForm(form)) !== JSON.stringify(saved);
	const savedEnabled = saved.recipients.filter((r) => r.enabled);

	return (
		<div className="space-y-3">
			<div className="max-w-3xl space-y-3">
				{log.data && !log.data.smsReady ? (
					<NoticeBanner
						tone="warning"
						title="SMSAPI nie jest skonfigurowane."
						description="Ani alert, ani testowy SMS nie wyjdą, dopóki wysyłka SMS nie zostanie włączona w konfiguracji modułu SMS."
					/>
				) : null}
				<section className="space-y-3 rounded-md border border-border bg-card p-3">
					<div className="space-y-1">
						<label
							className="flex items-center gap-2 text-sm font-medium"
							htmlFor="hd-alerts-enabled"
						>
							<input
								id="hd-alerts-enabled"
								type="checkbox"
								checked={form.enabled}
								onChange={(e) => set({ enabled: e.target.checked })}
							/>
							Alert SMS o krytycznych zgłoszeniach
						</label>
						<p className="text-[11px] text-muted-foreground">
							Nowe zgłoszenie apteki, które dziś blokuje jej pracę (sprzedaż lub recepty, serwer KS,
							internet, wszystkie stanowiska), idzie SMS-em na wszystkie włączone numery. Ocenia AI;
							gdy AI nie odpowie, decydują frazy niżej. Samo „pilne” nie wystarcza. SMS zawiera
							numer zgłoszenia, aptekę, jedno zdanie i link — bez treści maila.
						</p>
					</div>
					<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
						<HourRange
							label="Dni robocze"
							from={form.workdayFrom}
							to={form.workdayTo}
							onChange={(workdayFrom, workdayTo) => set({ workdayFrom, workdayTo })}
						/>
						<HourRange
							label="Weekendy i święta"
							from={form.holidayFrom}
							to={form.holidayTo}
							onChange={(holidayFrom, holidayTo) => set({ holidayFrom, holidayTo })}
						/>
					</div>
					<p className="text-[11px] text-muted-foreground">
						Czas warszawski, święta ustawowe (z Wigilią). Zgłoszenie spoza okna nie dostaje SMS-a —
						także później, bez odkładania na rano.
					</p>
				</section>

				<section className="space-y-2 rounded-md border border-border bg-card p-3">
					<h3 className="text-sm font-semibold">Odbiorcy</h3>
					{form.recipients.length ? (
						<div className="divide-y divide-border">
							{form.recipients.map((r, i) => (
								<div
									// biome-ignore lint/suspicious/noArrayIndexKey: wiersze bez id, kolejność zmienia tylko usuwanie
									key={i}
									className="grid grid-cols-1 items-center gap-2 py-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
								>
									<Input
										aria-label={`Odbiorca ${i + 1} — nazwa`}
										placeholder="Nazwa, np. Dyżur IT"
										maxLength={40}
										value={r.name}
										onChange={(e) => setRecipient(i, { name: e.target.value })}
										className="h-9 text-sm"
									/>
									<Input
										aria-label={`Odbiorca ${i + 1} — numer`}
										type="tel"
										inputMode="tel"
										placeholder="600 100 200"
										value={r.phone}
										onChange={(e) => setRecipient(i, { phone: e.target.value })}
										className="h-9 text-sm tabular-nums"
									/>
									<div className="flex items-center justify-between gap-2">
										<label className="flex items-center gap-1.5 text-xs">
											<input
												type="checkbox"
												checked={r.enabled}
												onChange={(e) => setRecipient(i, { enabled: e.target.checked })}
											/>
											włączony
										</label>
										<Button
											size="icon"
											variant="ghost"
											className="size-9"
											aria-label={`Usuń odbiorcę ${r.name || i + 1}`}
											onClick={() => set({ recipients: form.recipients.filter((_, j) => j !== i) })}
										>
											<Trash2 className="size-4" />
										</Button>
									</div>
								</div>
							))}
						</div>
					) : (
						<p className="text-xs text-muted-foreground">Brak numerów.</p>
					)}
					<Button
						size="sm"
						variant="outline"
						className="h-9"
						disabled={form.recipients.length >= MAX_RECIPIENTS}
						onClick={() =>
							set({ recipients: [...form.recipients, { name: "", phone: "", enabled: true }] })
						}
					>
						<Plus className="mr-1.5 size-4" />
						Dodaj numer
					</Button>
				</section>

				<section className="space-y-1 rounded-md border border-border bg-card p-3">
					<label htmlFor="hd-alerts-phrases" className="text-sm font-semibold">
						Frazy zapasowe
					</label>
					<p className="text-[11px] text-muted-foreground">
						Jedna w linii. Używane tylko wtedy, gdy AI zwróci błąd, przekroczy czas, nie oceni
						krytyczności albo jest wyłączone; porównanie bez wielkości liter i polskich znaków,
						całymi słowami, tylko w mailach od ludzi (bez autoodpowiedzi). Fraza trafia do
						dziennika, nie do SMS-a. Fraza złożona z samych ponagleń („pilne”, „na cito”) jest
						odrzucana.
					</p>
					<textarea
						id="hd-alerts-phrases"
						value={form.phrases}
						onChange={(e) => set({ phrases: e.target.value })}
						className="min-h-28 w-full rounded-md border border-input bg-background p-2 text-sm"
					/>
				</section>

				<section className="space-y-2 rounded-md border border-border bg-card p-3">
					<h3 className="text-sm font-semibold">Limity</h3>
					<div className="grid grid-cols-1 gap-3 md:grid-cols-3">
						<NumberField
							id="hd-alerts-pharmacy"
							label="Na aptekę: 1 SMS na [h]"
							value={form.pharmacyHours}
							onChange={(pharmacyHours) => set({ pharmacyHours })}
						/>
						<NumberField
							id="hd-alerts-hour"
							label="Na godzinę"
							value={form.perHour}
							onChange={(perHour) => set({ perHour })}
						/>
						<NumberField
							id="hd-alerts-day"
							label="Na dobę"
							value={form.perDay}
							onChange={(perDay) => set({ perDay })}
						/>
					</div>
					<p className="text-[11px] text-muted-foreground">
						Zawsze najwyżej 1 SMS na zgłoszenie. Po przekroczeniu limitu godzinowego albo dobowego
						wychodzi jeden SMS „limit wyczerpany” i do końca dnia jest cisza. Nieudana wysyłka też
						zajmuje limit. Awaria — 3 apteki w 30 min z podobną treścią — to jeden SMS zbiorczy;
						zajmuje limit każdej apteki z grupy. Limity liczą się dla całej organizacji.
					</p>
				</section>

				<div className="flex flex-wrap items-center justify-end gap-2">
					{dirty ? (
						<span className="text-xs text-muted-foreground">
							Niezapisane zmiany — test używa zapisanych.
						</span>
					) : null}
					<Button
						variant="outline"
						disabled={test.isPending || dirty || !savedEnabled.length || !log.data?.smsReady}
						onClick={() => setConfirmTest(true)}
					>
						{test.isPending ? (
							<Loader2 className="mr-1.5 size-4 animate-spin" />
						) : (
							<Send className="mr-1.5 size-4" />
						)}
						Wyślij testowy SMS
					</Button>
					<Button disabled={save.isPending} onClick={() => save.mutate(fromForm(form))}>
						{save.isPending ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : null}
						Zapisz
					</Button>
				</div>
			</div>

			<AlertLog items={log.data?.items ?? []} loading={log.isLoading} />

			<ConfirmDialog
				open={confirmTest}
				onOpenChange={setConfirmTest}
				title="Wysłać testowy SMS?"
				description={`Na włączone numery (${savedEnabled.length}): ${savedEnabled.map((r) => r.name).join(", ")}. Bez okna godzin i limitów; wpis trafi do dziennika.`}
				confirmLabel="Wyślij"
				isLoading={test.isPending}
				onConfirm={() => test.mutate(undefined, { onSettled: () => setConfirmTest(false) })}
			/>
		</div>
	);
}

function AlertLog({ items, loading }: { items: AlertLogItem[]; loading: boolean }) {
	const tableExport = useTableExport();
	const columns = useMemo<ColumnDef<AlertLogItem>[]>(
		() => [
			{
				accessorKey: "createdAt",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Kiedy" />,
				cell: ({ row }) => (
					<span className="tabular-nums">{formatDateTime(row.original.createdAt)}</span>
				),
				size: 130,
				meta: { isDate: true, mobileRole: "subtitle" },
			},
			{
				id: "kind",
				accessorFn: (r) => ALERT_KIND_LABEL[r.kind],
				header: ({ column }) => <DataTableColumnHeader column={column} title="Rodzaj" />,
				size: 130,
				meta: {
					mobileRole: "title",
					facetedOptions: Object.values(ALERT_KIND_LABEL).map((label) => ({ value: label, label })),
				},
			},
			{
				id: "tickets",
				accessorFn: (r) =>
					(r.numbers.length ? r.numbers : r.ticketNumber ? [r.ticketNumber] : [])
						.map((n) => `#${n}`)
						.join(" "),
				header: ({ column }) => <DataTableColumnHeader column={column} title="Zgłoszenia" />,
				cell: ({ getValue }) => <span className="font-mono">{String(getValue() || "—")}</span>,
				size: 160,
				meta: { mobileRole: "subtitle" },
			},
			{
				id: "status",
				accessorFn: (r) => ALERT_STATUS[r.status].label,
				header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
				cell: ({ row }) => (
					<span className={ALERT_STATUS[row.original.status].className}>
						{ALERT_STATUS[row.original.status].label}
					</span>
				),
				size: 100,
				meta: {
					facetedOptions: Object.values(ALERT_STATUS).map(({ label }) => ({ value: label, label })),
				},
			},
			{
				id: "delivered",
				accessorFn: (r) =>
					`${r.recipients.filter((x) => x.status === "sent").length}/${r.recipients.length}`,
				header: ({ column }) => <DataTableColumnHeader column={column} title="Wysłane" />,
				cell: ({ getValue }) => <span className="tabular-nums">{String(getValue())}</span>,
				size: 90,
			},
			{
				accessorKey: "reason",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Powód" />,
				cell: ({ row }) => <span className="truncate">{row.original.reason || "—"}</span>,
				size: 240,
				meta: { fill: true },
			},
			{
				accessorKey: "body",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Treść SMS" />,
				cell: ({ row }) => <span className="truncate">{row.original.body}</span>,
				size: 280,
				meta: { mobileRole: "detail" },
			},
			{
				id: "error",
				accessorFn: (r) => r.error ?? "",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Błąd" />,
				cell: ({ getValue }) => (
					<span className="truncate text-status-error-text">{String(getValue())}</span>
				),
				size: 220,
				meta: { mobileRole: "detail" },
			},
		],
		[],
	);
	return (
		<section className="flex flex-col gap-2">
			<div className="flex items-center gap-2">
				<h2 className="text-sm font-semibold">Dziennik prób</h2>
				<span className="truncate text-xs text-muted-foreground">
					Każda próba, także nieudana; ostatnie 200.
				</span>
				{loading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
				<Button
					size="sm"
					variant="outline"
					className="ml-auto h-8 text-xs"
					disabled={!tableExport.ready || tableExport.exporting}
					onClick={tableExport.run}
				>
					Eksportuj
				</Button>
			</div>
			<div className="flex min-h-[16rem] flex-col rounded-md border border-border bg-card">
				<DataTable
					tableId="helpdesk.alert-log"
					className="min-h-0 w-full flex-1"
					columns={columns}
					data={items}
					getRowId={(r) => String(r.id)}
					enableToolbar
					enableColumnVisibility
					onExportReady={tableExport.onExportReady}
					exportFileName="Helpdesk_alerty_SMS"
					columnLabels={LOG_COLUMN_LABELS}
				/>
			</div>
		</section>
	);
}
